/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { SponsoredInitiative, Subscription, SubscriptionStatus } from '../../types/initiative';
import { DATE, formatDate } from '../../utils/date';
import { Sponsorship } from '@g17eco/types/sponsorship';

export interface SponsoredCompanyRow {
  _id: string;
  name: string;
  customerId: string;
  canBeSponsored: boolean;
  subscriptionId: string;
  status: Subscription['status'];
  startDate: number;
  endDate?: number;
  cancelDate?: number;
  periodStart: number;
  periodEnd: number;
  discountStart: number;
  discountEnd: number;
  subscriptions: Subscription[];
  sponsorships: Sponsorship[];
}

const hydrator = (sub: Subscription) => {
  return {
    status: sub.status,
    subscriptionId: sub.id,
    startDate: sub.startDate,
    endDate: sub.endDate,
    cancelDate: sub.cancelDate,
    periodStart: sub.periodStart,
    periodEnd: sub.cancelDate ?? sub.endDate ?? sub.periodEnd,
    discountStart: sub.discount?.start ?? 0,
    discountEnd: sub.cancelDate ?? sub.endDate ?? sub.discount?.end ?? 0,
  }
}

export const convertToTableRows = (data: SponsoredInitiative[]): SponsoredCompanyRow[] => {
  const company = new Map<string, SponsoredCompanyRow>();

  data.forEach(c => {
    if (!company.has(c._id)) {
      company.set(c._id, {
        _id: c._id,
        name: c.name,
        customerId: c.customerId,
        canBeSponsored: c.canBeSponsored,
        ...hydrator(c.subscription),
        subscriptions: [],
        sponsorships: [],
      });
    }
    const current = company.get(c._id);
    if (!current) {
      // Typescript breakout, it shouldn't be possible
      return;
    }


    current.sponsorships.concat(c.sponsorships);
    current.subscriptions.push(c.subscription);
    if (isLatest(current, c.subscription)) {
      company.set(c._id, {
        ...current,
        ...hydrator(c.subscription)
      });
    }
  });


  return Array.from(company.values());
}

const activeStatus: SubscriptionStatus[] = ['active', 'trialing'];

export const isSponsorshipActive = (company: SponsoredCompanyRow): boolean => {
  return activeStatus.includes(company.status);
}

const isLatest = (company: SponsoredCompanyRow, subscription: SponsoredInitiative['subscription']): boolean => {
  const currentActive = activeStatus.includes(company.status);
  const incomingActive = activeStatus.includes(subscription.status);

  if (currentActive && !incomingActive) {
    // Always prioritize active sub
    return false;
  }
  if (incomingActive && !currentActive) {
    // Always prioritize active sub
    return true;
  }

  if (company.periodEnd < subscription.periodEnd) {
    return true;
  }
  return false;
}

export const fmtDate = (date?: number | null, format = DATE.DEFAULT_DASHES) => date ? formatDate(new Date(date * 1000), format) : '-';

export const pcComplete = (start: number, end: number, now: number): number => {
  if (now > end) {
    return 100;
  }
  if (now < start) {
    return 0;
  }
  return Math.round(1000 * (now - start) / (end - start)) / 10;
}

export const getSubscriptionStartDate = (sub: Subscription): number => {
  return sub.startDate;
}

export const getSubscriptionEndDate = (sub: Subscription): number => {
  return sub.cancelDate ?? sub.endDate ?? sub.periodEnd;
}
